import { useState } from "react";
import usePageLoader from '../../hooks/usePageLoader';
import { ShimmerStatCard, ShimmerBox, ShimmerCircle, ShimmerTable } from '../../components/common/Shimmer';
import {
  FiDownload, FiUser, FiMail, FiPhone, FiTrash2,
  FiCheckCircle, FiSearch, FiTrendingUp, FiLink2,
} from "react-icons/fi";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Pagination from "../../components/common/Pagination";
import toast from "react-hot-toast";

/* ─── Mock data ─── */
const INITIAL_LEADS = [
  { id: 1,  name: "Arun Kumar",     email: "arun@gmail.com",        phone: "+91 98765 43210", source: "/pricing",      chatbot: "Support Bot",    captured: "2h ago",   status: "new",       country: "🇮🇳 India"     },
  { id: 2,  name: "Priya Sharma",   email: "priya@company.com",     phone: "+91 87654 32109", source: "/",             chatbot: "Support Bot",    captured: "5h ago",   status: "contacted", country: "🇮🇳 India"     },
  { id: 3,  name: "Rahul Verma",    email: "rahul@startup.io",      phone: "+91 76543 21098", source: "/features",     chatbot: "Sales Bot",      captured: "1d ago",   status: "qualified", country: "🇮🇳 India"     },
  { id: 4,  name: "Meena Iyer",     email: "meena@corp.com",        phone: "",                source: "/pricing",      chatbot: "Support Bot",    captured: "2d ago",   status: "new",       country: "🇮🇳 India"     },
  { id: 5,  name: "Suresh Nair",    email: "suresh@agency.co",      phone: "+91 65432 10987", source: "/contact",      chatbot: "Sales Bot",      captured: "3d ago",   status: "converted", country: "🇮🇳 India"     },
  { id: 6,  name: "Anjali Singh",   email: "anjali@mail.com",       phone: "+91 54321 09876", source: "/",             chatbot: "Support Bot",    captured: "4d ago",   status: "contacted", country: "🇸🇬 Singapore" },
  { id: 7,  name: "David Kim",      email: "david@techfirm.com",    phone: "+1 415 234 5678", source: "/integrations", chatbot: "Sales Bot",      captured: "4d ago",   status: "qualified", country: "🇺🇸 USA"       },
  { id: 8,  name: "Sarah Johnson",  email: "sarah@saas.io",         phone: "+1 312 987 6543", source: "/pricing",      chatbot: "Onboarding Bot", captured: "5d ago",   status: "converted", country: "🇺🇸 USA"       },
  { id: 9,  name: "Ravi Patel",     email: "ravi@enterprise.in",    phone: "+91 93456 78901", source: "/solutions",    chatbot: "Sales Bot",      captured: "5d ago",   status: "new",       country: "🇮🇳 India"     },
  { id: 10, name: "Emma Wilson",    email: "emma@design.co.uk",     phone: "+44 7700 900123", source: "/features",     chatbot: "Support Bot",    captured: "6d ago",   status: "contacted", country: "🇬🇧 UK"        },
  { id: 11, name: "Carlos Ruiz",    email: "carlos@latam.co",       phone: "+52 55 1234 5678",source: "/",             chatbot: "Sales Bot",      captured: "7d ago",   status: "new",       country: "🇲🇽 Mexico"    },
  { id: 12, name: "Fatima Al-Said", email: "fatima@gulf.ae",        phone: "+971 50 123 4567",source: "/pricing",      chatbot: "Onboarding Bot", captured: "8d ago",   status: "qualified", country: "🇦🇪 UAE"       },
  { id: 13, name: "John Park",      email: "john@korea.co",         phone: "+82 10 1234 5678",source: "/integrations", chatbot: "Support Bot",    captured: "9d ago",   status: "lost",      country: "🇰🇷 Korea"     },
  { id: 14, name: "Neha Gupta",     email: "neha@fintech.in",       phone: "+91 88765 43210", source: "/features",     chatbot: "Sales Bot",      captured: "10d ago",  status: "converted", country: "🇮🇳 India"     },
  { id: 15, name: "Tom Mitchell",   email: "tom@aussie.com.au",     phone: "+61 400 123 456", source: "/pricing",      chatbot: "Onboarding Bot", captured: "12d ago",  status: "contacted", country: "🇦🇺 Australia" },
];

const LEADS_TREND = [
  { day: "May 5",  leads: 2 },
  { day: "May 6",  leads: 1 },
  { day: "May 7",  leads: 3 },
  { day: "May 8",  leads: 2 },
  { day: "May 9",  leads: 4 },
  { day: "May 10", leads: 2 },
  { day: "May 11", leads: 3 },
];

const STATUS_META = {
  new:       { label: "New",       cls: "bg-blue-100 text-blue-700"    },
  contacted: { label: "Contacted", cls: "bg-yellow-100 text-yellow-700"},
  qualified: { label: "Qualified", cls: "bg-indigo-100 text-indigo-700"},
  converted: { label: "Converted", cls: "bg-green-100 text-green-700"  },
  lost:      { label: "Lost",      cls: "bg-gray-100 text-gray-500"    },
};

const STATUS_CYCLE = { new: "contacted", contacted: "qualified", qualified: "converted", converted: "converted", lost: "lost" };

const CRM_INTEGRATIONS = [
  { name: "HubSpot",    logo: "🟠", desc: "Sync leads to HubSpot CRM automatically",     connected: true  },
  { name: "Salesforce", logo: "☁️", desc: "Push qualified leads to Salesforce pipeline", connected: false },
  { name: "Zapier",     logo: "⚡", desc: "Connect to 5000+ apps via Zapier workflows",  connected: false },
];

const PAGE_SIZE = 8;

function LeadsSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <ShimmerStatCard key={i} />)}
      </div>
      <div className="bg-white border rounded-xl p-4">
        <ShimmerBox h="h-32" w="w-full" rounded="rounded-lg" />
      </div>
      <ShimmerTable rows={8} cols={9} />
    </div>
  );
}

/* ─── Page ─── */
export default function Leads() {
  const [leads, setLeads]           = useState(INITIAL_LEADS);
  const loading = usePageLoader();
  const [search, setSearch]         = useState("");
  const [statusFilter, setStatus]   = useState("all");
  const [chatbotFilter, setChatbot] = useState("all");
  const [selected, setSelected]     = useState([]);
  const [page, setPage]             = useState(1);

  /* Derived */
  const chatbots   = [...new Set(INITIAL_LEADS.map((l) => l.chatbot))];
  const newToday   = leads.filter((l) => l.captured.includes("h ago")).length;
  const converted  = leads.filter((l) => l.status === "converted").length;
  const withPhone  = leads.filter((l) => l.phone).length;

  const filtered = leads.filter((l) => {
    const q = search.toLowerCase();
    const matchSearch = !q || l.name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q) || l.country.toLowerCase().includes(q);
    const matchStatus = statusFilter === "all" || l.status === statusFilter;
    const matchBot    = chatbotFilter === "all" || l.chatbot === chatbotFilter;
    return matchSearch && matchStatus && matchBot;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* Actions */
  const advanceStatus = (id) => {
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status: STATUS_CYCLE[l.status] } : l));
    toast.success("Status updated.");
  };

  const deleteLead = (id) => {
    setLeads((prev) => prev.filter((l) => l.id !== id));
    setSelected((prev) => prev.filter((s) => s !== id));
    toast.success("Lead removed.");
  };

  const deleteSelected = () => {
    setLeads((prev) => prev.filter((l) => !selected.includes(l.id)));
    setSelected([]);
    toast.success(`${selected.length} lead(s) removed.`);
  };

  const toggleSelect = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelected(selected.length === paginated.length ? [] : paginated.map((l) => l.id));

  const exportCSV = () => {
    const src = selected.length ? leads.filter((l) => selected.includes(l.id)) : leads;
    const headers = ["Name", "Email", "Phone", "Country", "Source Page", "Chatbot", "Status", "Captured"];
    const rows = src.map((l) => [l.name, l.email, l.phone, l.country.replace(/[^\w\s,]/g, ""), l.source, l.chatbot, l.status, l.captured].join(","));
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"), { href: url, download: "leads.csv" }).click();
    toast.success("CSV exported.");
  };

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* ── Sticky Header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Leads</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {leads.length} visitors who shared contact info via chatbot
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={chatbotFilter}
            onChange={(e) => { setChatbot(e.target.value); setPage(1); }}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Chatbots</option>
            {chatbots.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 transition"
          >
            <FiDownload size={14} />
            {selected.length ? `Export ${selected.length} Selected` : "Export CSV"}
          </button>
        </div>
      </div>

      {loading ? <LeadsSkeleton /> : <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* ── KPI + Trend ── */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">

          {/* KPI cards */}
          <div className="xl:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard icon={FiUser}       label="Total Leads"   value={leads.length}  sub="all time"        color="text-primary" />
            <KpiCard icon={FiTrendingUp} label="New Today"     value={newToday}      sub="last 12 hrs"     color="text-blue-500" />
            <KpiCard icon={FiCheckCircle}label="Converted"     value={converted}     sub={`${Math.round((converted/leads.length)*100)}% rate`} color="text-green-500" />
            <KpiCard icon={FiPhone}      label="With Phone"    value={withPhone}     sub={`${leads.length - withPhone} missing`} color="text-orange-500" />
          </div>

          {/* Leads trend chart */}
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Last 7 Days</p>
            <ResponsiveContainer width="100%" height={80}>
              <AreaChart data={LEADS_TREND} margin={{ top: 2, right: 2, left: -28, bottom: 0 }}>
                <defs>
                  <linearGradient id="lgLead" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#4F46E5" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="day" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} />
                <Tooltip />
                <Area type="monotone" dataKey="leads" stroke="#4F46E5" strokeWidth={2} fill="url(#lgLead)" dot={{ r: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Table card ── */}
        <div className="bg-white border rounded-xl overflow-hidden">

          {/* Toolbar */}
          <div className="px-5 py-3 border-b flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1.5 w-56">
              <FiSearch size={13} className="text-gray-400 shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search name, email, country…"
                className="bg-transparent text-sm flex-1 outline-none text-gray-700 placeholder-gray-400"
              />
            </div>

            {/* Status filter tabs */}
            <div className="flex items-center border rounded-lg overflow-hidden text-xs font-medium">
              {[
                { key: "all",       label: `All (${leads.length})` },
                { key: "new",       label: `New (${leads.filter(l=>l.status==="new").length})` },
                { key: "contacted", label: `Contacted (${leads.filter(l=>l.status==="contacted").length})` },
                { key: "qualified", label: `Qualified (${leads.filter(l=>l.status==="qualified").length})` },
                { key: "converted", label: `Converted (${leads.filter(l=>l.status==="converted").length})` },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => { setStatus(t.key); setPage(1); }}
                  className={`px-3 py-1.5 transition ${
                    statusFilter === t.key ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-2">
              {selected.length > 0 && (
                <button
                  onClick={deleteSelected}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition"
                >
                  <FiTrash2 size={12} /> Delete {selected.length}
                </button>
              )}
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={paginated.length > 0 && selected.length === paginated.length}
                      onChange={toggleAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-left">Country</th>
                  <th className="px-4 py-3 text-left">Source</th>
                  <th className="px-4 py-3 text-left">Chatbot</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-left">Captured</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginated.map((lead) => {
                  const sm = STATUS_META[lead.status];
                  return (
                    <tr
                      key={lead.id}
                      className={`hover:bg-gray-50 transition ${selected.includes(lead.id) ? "bg-primary/5" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.includes(lead.id)}
                          onChange={() => toggleSelect(lead.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                            {lead.name[0]}
                          </div>
                          <span className="font-medium text-gray-900">{lead.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{lead.email}</td>
                      <td className="px-4 py-3 text-gray-600">{lead.phone || <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{lead.country}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{lead.source}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                          {lead.chatbot}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => advanceStatus(lead.id)}
                          className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${sm.cls} hover:opacity-80 transition`}
                          title={lead.status !== "converted" && lead.status !== "lost" ? `Advance to ${STATUS_CYCLE[lead.status]}` : ""}
                        >
                          {sm.label}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{lead.captured}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => deleteLead(lead.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition"
                          title="Delete lead"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {paginated.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-5 py-12 text-center text-gray-400">
                      No leads match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── CRM Integrations ── */}
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">CRM Integrations</h2>
              <p className="text-xs text-gray-500 mt-0.5">Auto-sync leads to your CRM as they come in</p>
            </div>
            <FiLink2 size={16} className="text-gray-400" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-x">
            {CRM_INTEGRATIONS.map((crm) => (
              <div key={crm.name} className="px-6 py-5 flex items-start gap-4">
                <span className="text-2xl mt-0.5">{crm.logo}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-gray-900 text-sm">{crm.name}</p>
                    {crm.connected && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Connected</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 mb-3">{crm.desc}</p>
                  <button className={`text-xs px-3 py-1.5 rounded-lg font-medium transition ${
                    crm.connected
                      ? "border text-gray-600 hover:bg-gray-50"
                      : "bg-primary text-white hover:opacity-90"
                  }`}>
                    {crm.connected ? "Manage" : "Connect"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <Icon className={color} size={15} />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
    </div>
  );
}
